import FuturesConfigurationFields from '@/features/strategyBacktests/FuturesConfigurationFields';
import { emptyFuturesConfiguration, parseFuturesConfiguration } from '@/features/strategyBacktests/futuresConfigurationDraft';
import { marketGroupLabel } from '../reportView';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import ScreenerUniverseSelector from '@/components/Screeners/ScreenerUniverseSelector';
import {
    Button,
    Callout,
    FormCheckbox,
    FormDateInput,
    FormInput,
    FormNumberInput,
    FormSelect,
    FormTimeInput,
    Modal,
    SegmentedControl
} from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import EntitySelectionPanel from '@/components/UI/EntitySelectionPanel';
import OptionBuyingStrategySelect from '@/components/UI/OptionBuyingStrategySelect';
import { OPTIONS_DATA_START_DATETIME } from '@/config/constants';
import { useAppQuery } from '@/hooks/useAppQuery';
import { getTimeHorizonsForDuration, getSupportedTimeHorizonsForDuration, isEventHorizon } from '@/utils/timeHorizonGenerator';
import { getResearchWindow } from '@einhaus/project314-shared';
import {
    describeSessionTimingPolicy,
    formatHorizonLabel,
    runnableVariants,
    type CreateIdeaLabRunInput,
    type IdeaLabRunDetail,
    type IdeaLabRunPreview,
    type IdeaLabStudyDetail,
    type IdeaLabVariantSummary
} from '../ideaLabUtils';
import {
    buildDefaultRunFormState,
    buildRunFormStateFromRun,
    buildSessionTimingModePolicy,
    joinDateTime,
    type RunFormState,
    type SessionTimingMode,
    type UniverseMode
} from './runFormState';

/** Durations the platform option random-entry baselines are materialized for. */
const OPTION_BASELINE_DURATIONS = new Set(['30m', '60m', '240m', '1d', '1d_ah', '1d_noah']);

const UNIVERSE_MODE_OPTIONS: { value: UniverseMode; label: string }[] = [
    { value: 'entities', label: 'Entity list' },
    { value: 'screener', label: 'Screener' }
];

const SESSION_TIMING_OPTIONS: { value: SessionTimingMode; label: string }[] = [
    { value: 'recommended', label: 'Idea Lab recommended' },
    { value: 'nearClose', label: 'Near close' },
    { value: 'atClosePlus1s', label: 'Completed bar' },
    { value: 'nextOpen', label: 'Next bar / session open' }
];

const parseSessionTimingMode = (value: string): SessionTimingMode => {
    const option = SESSION_TIMING_OPTIONS.find((candidate) => candidate.value === value);

    if (!option) throw new Error(`Unsupported session timing mode: ${value}`);
    return option.value;
};

/** The form's source: blank, or the id of a run whose settings pre-fill it. */
type FormSourceKey = 'blank' | number;

interface CreateRunModalProps {
    study: IdeaLabStudyDetail;
    isOpen: boolean;
    /** Pre-fills every setting from this run ("Run again with changes"); null opens a blank form. */
    sourceRunId?: number | null;
    onClose: () => void;
    onCreated: (run: IdeaLabRunDetail) => void;
}

/** Every frozen variant sharing the first frozen variant's duration. */
const defaultVariantIds = (frozen: readonly IdeaLabVariantSummary[]): number[] => {
    const duration = frozen[0]?.primaryDuration;

    return frozen.filter((variant) => variant.primaryDuration === duration).map((variant) => variant.id);
};

/**
 * Queues a run: freezes the universe, the Eastern window with its knowledge cutoff, the horizons,
 * and each selected frozen variant's compiled artifact, then fans work out to the elastic fleet.
 */
const CreateRunModal = ({ study, isOpen, sourceRunId = null, onClose, onCreated }: CreateRunModalProps) => {
    const queryClient = useQueryClient();
    const defaultWindow = useMemo(() => getResearchWindow(OPTIONS_DATA_START_DATETIME, new Date()), []);
    const frozen = useMemo(() => runnableVariants(study.variants), [study.variants]);

    const [form, setForm] = useState<RunFormState>(() => buildDefaultRunFormState(defaultWindow));
    const [appliedSource, setAppliedSource] = useState<FormSourceKey | null>(null);
    const [droppedVariantCount, setDroppedVariantCount] = useState(0);
    const [previewState, setPreviewState] = useState<{ key: string; preview: IdeaLabRunPreview } | null>(null);
    const [isPreviewing, setIsPreviewing] = useState(false);

    const patchForm = (patch: Partial<RunFormState>) => setForm((current) => ({ ...current, ...patch }));

    const futuresRoutes = useAppQuery({
        ...trpc.strategy.fetchFuturesSignalRouteCatalog.queryOptions(),
        enabled: isOpen && form.includeFutures
    });
    const futuresSelection = useMemo(() => {
        if (!form.includeFutures) return { selections: undefined, error: null };
        try {
            if (form.universeMode !== 'entities' || !form.selectedEntities.length || form.selectedEntities.length > 2)
                throw new Error('Choose SPY, QQQ, or both as the ETF signal subjects.');
            const selections = form.selectedEntities.map((entity) => {
                const route = futuresRoutes.data?.find((route) => entity.entityType === 'Asset' && route.signalAssetId === entity.entityId);
                if (!route) throw new Error('The futures lens supports SPY→MES and QQQ→MNQ only.');
                const draft = form.futuresDrafts[entity.entityId];
                if (!draft) throw new Error(`Choose costs and allocation for ${route.signalSymbol}→${route.productSymbol}.`);
                const { signalRouteId: _route, ...execution } = parseFuturesConfiguration(draft);
                if (execution.productId !== route.productId) throw new Error('The execution product differs from its ETF route.');
                return { signalAssetId: route.signalAssetId, execution };
            });
            return { selections, error: null };
        } catch (error) {
            return { selections: undefined, error: error instanceof Error ? error.message : 'Invalid futures settings' };
        }
    }, [form.includeFutures, form.universeMode, form.selectedEntities, form.futuresDrafts, futuresRoutes.data]);

    const sourceRunQuery = useAppQuery(
        {
            ...trpc.ideaLab.getRun.queryOptions({ runId: sourceRunId ?? 0 }),
            enabled: isOpen && sourceRunId !== null
        },
        { fallbackErrorMessage: 'Failed to load the run to copy' }
    );
    const sourceRun = sourceRunId !== null ? (sourceRunQuery.data ?? null) : null;

    // A blank open keeps earlier edits but re-defaults the variants and horizons; a source run
    // replaces the whole form once, so reopening the same copy keeps its edits too.
    useEffect(() => {
        if (!isOpen) return;

        if (sourceRunId === null) {
            const base = appliedSource === 'blank' || appliedSource === null ? null : buildDefaultRunFormState(defaultWindow);

            setForm((current) => ({ ...(base ?? current), selectedVariantIds: defaultVariantIds(frozen), selectedHorizons: null }));
            setDroppedVariantCount(0);
            if (appliedSource !== 'blank') setAppliedSource('blank');
            return;
        }

        if (appliedSource === sourceRunId || sourceRun === null) return;

        const prefill = buildRunFormStateFromRun(
            sourceRun,
            new Set(frozen.map((variant) => variant.id)),
            buildDefaultRunFormState(defaultWindow)
        );

        setForm(prefill.state);
        setDroppedVariantCount(prefill.droppedVariantCount);
        setAppliedSource(sourceRunId);
    }, [appliedSource, defaultWindow, frozen, isOpen, sourceRun, sourceRunId]);

    const isLoadingSource = sourceRunId !== null && appliedSource !== sourceRunId;

    const selectedVariants = useMemo(
        () => frozen.filter((variant) => form.selectedVariantIds.includes(variant.id)),
        [frozen, form.selectedVariantIds]
    );
    const durations = useMemo(() => [...new Set(selectedVariants.map((variant) => variant.primaryDuration))], [selectedVariants]);
    const duration = durations.length === 1 ? durations[0]! : null;
    const horizonMenu = useMemo(
        () =>
            duration
                ? getSupportedTimeHorizonsForDuration(duration).filter(
                      (horizon) =>
                          !isEventHorizon(horizon) && (!form.includeFutures || (horizon >= 60 && horizon <= 3600 && horizon % 60 === 0))
                  )
                : [],
        [duration, form.includeFutures]
    );
    const defaultHorizons = useMemo(() => (duration ? getTimeHorizonsForDuration(duration) : []), [duration]);
    const effectiveHorizons = form.selectedHorizons ?? defaultHorizons;
    const minuteDuration = duration !== null && duration.endsWith('m') && !duration.endsWith('mo');
    const optionLensRequested = form.includeOptions && form.optionBuyingStrategyId !== null;
    const optionBaselinesAvailable = duration !== null && OPTION_BASELINE_DURATIONS.has(duration);
    const sessionTimingPolicy: CreateIdeaLabRunInput['sessionTimingPolicy'] = useMemo(() => {
        if (duration === null || form.sessionTimingMode === 'recommended') return undefined;

        return { [duration]: buildSessionTimingModePolicy(form.sessionTimingMode, form.preCloseOffsetSecs) };
    }, [duration, form.preCloseOffsetSecs, form.sessionTimingMode]);

    const createMutation = useMutation({
        ...trpc.ideaLab.createRun.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (run) => {
            showToast(`Run "${run.label}" queued with ${run.workUnitCount} work units`, 'success');
            await queryClient.invalidateQueries(trpc.ideaLab.listRuns.pathFilter());
            onCreated(run);
        }
    });

    const universe: CreateIdeaLabRunInput['universe'] | null = useMemo(() => {
        if (form.universeMode === 'screener') {
            return form.screenerRuleId === null
                ? null
                : {
                      type: 'screenerRuleId',
                      screenerRuleId: form.screenerRuleId,
                      ...(form.screenerEntityTypes.length > 0 ? { entityTypes: form.screenerEntityTypes } : {})
                  };
        }

        if (form.selectedEntities.length === 0) return null;

        return form.entityListId !== null
            ? { type: 'resolvedFromEntityList', sourceEntityListId: form.entityListId, entities: form.selectedEntities }
            : { type: 'entities', entities: form.selectedEntities };
    }, [form.universeMode, form.screenerRuleId, form.screenerEntityTypes, form.selectedEntities, form.entityListId]);

    const startDateTime = joinDateTime(form.start);
    const endDateTime = joinDateTime(form.end);
    const walkForwardStartDateTime = form.useWalkForward ? joinDateTime(form.walkForwardStart) : null;

    const validation = useMemo(() => {
        if (isLoadingSource) return 'Loading the run to copy…';
        if (form.label.trim() === '') return 'Give the run a label.';
        if (frozen.length === 0) return 'Freeze at least one variant before running.';
        if (selectedVariants.length === 0) return 'Select at least one frozen variant.';
        if (duration === null) return 'Selected variants span more than one duration; a run evaluates one duration.';
        if (universe === null) return form.universeMode === 'screener' ? 'Choose a screener rule.' : 'Choose an entity list or entities.';
        if (!(startDateTime < endDateTime)) return 'The window start must precede its end.';
        if (walkForwardStartDateTime !== null && !(startDateTime < walkForwardStartDateTime && walkForwardStartDateTime < endDateTime)) {
            return 'The walk-forward start must lie strictly inside the window.';
        }
        if (effectiveHorizons.length === 0) return 'Select at least one horizon.';
        if (form.sessionTimingMode === 'nearClose' && form.preCloseOffsetSecs === null) return 'Enter a near-close offset.';
        if (!form.includeUnderlying && !form.includeOptions && !form.includeFutures) return 'Choose an outcome lens.';
        if (form.includeFutures && duration !== '1m') return 'The futures lens requires 1m variants.';
        if (futuresSelection.error) return futuresSelection.error;
        if (
            form.includeFutures &&
            (!form.afterHoursRemoved ||
                form.sessionTimingMode !== 'nextOpen' ||
                effectiveHorizons.some((horizon) => horizon < 60 || horizon > 3600 || horizon % 60 !== 0))
        )
            return 'Use regular-session completed 1m signals, next-open timing and horizons of 1–60 minutes for futures.';
        if (form.includeOptions && form.optionBuyingStrategyId === null)
            return 'Choose the option buying strategy that selects the contracts.';
        if (form.includeOptions && form.optionType === null) return 'Choose Call or Put for the option lens.';

        return null;
    }, [
        isLoadingSource,
        form.label,
        frozen.length,
        selectedVariants.length,
        duration,
        universe,
        form.universeMode,
        startDateTime,
        endDateTime,
        walkForwardStartDateTime,
        effectiveHorizons.length,
        form.sessionTimingMode,
        form.preCloseOffsetSecs,
        form.includeUnderlying,
        form.includeOptions,
        form.includeFutures,
        form.afterHoursRemoved,
        effectiveHorizons,
        futuresSelection.error,
        form.optionBuyingStrategyId,
        form.optionType
    ]);

    const proposedRun: CreateIdeaLabRunInput | null =
        validation === null && universe !== null
            ? {
                  studyId: study.id,
                  label: form.label.trim(),
                  variantIds: form.selectedVariantIds,
                  universe,
                  startDateTime,
                  endDateTime,
                  walkForwardStartDateTime,
                  horizonSeconds: effectiveHorizons,
                  afterHoursRemoved: form.afterHoursRemoved,
                  ...(futuresSelection.selections ? { futures: futuresSelection.selections } : {}),
                  ...(sessionTimingPolicy === undefined ? {} : { sessionTimingPolicy }),
                  includeUnderlying: form.includeUnderlying,
                  optionBuyingStrategyId: optionLensRequested ? form.optionBuyingStrategyId : null,
                  optionType: optionLensRequested ? form.optionType : null
              }
            : null;
    const previewKey = proposedRun === null ? null : JSON.stringify({ ...proposedRun, label: undefined });
    const preview = previewState?.key === previewKey ? previewState.preview : null;

    const loadPreview = async () => {
        if (proposedRun === null || previewKey === null) return;

        setIsPreviewing(true);

        try {
            const result = await queryClient.fetchQuery(
                trpc.ideaLab.previewRun.queryOptions({
                    studyId: proposedRun.studyId,
                    variantIds: proposedRun.variantIds,
                    universe: proposedRun.universe,
                    startDateTime: proposedRun.startDateTime,
                    endDateTime: proposedRun.endDateTime,
                    walkForwardStartDateTime: proposedRun.walkForwardStartDateTime,
                    horizonSeconds: proposedRun.horizonSeconds,
                    afterHoursRemoved: proposedRun.afterHoursRemoved,
                    sessionTimingPolicy: proposedRun.sessionTimingPolicy,
                    includeUnderlying: proposedRun.includeUnderlying,
                    futures: proposedRun.futures,
                    optionBuyingStrategyId: proposedRun.optionBuyingStrategyId,
                    optionType: proposedRun.optionType
                })
            );

            setPreviewState({ key: previewKey, preview: result });
        } catch (error) {
            showToast(error instanceof Error ? error.message : 'Failed to preview the run', 'error');
        } finally {
            setIsPreviewing(false);
        }
    };

    const submit = () => {
        if (proposedRun === null) return;

        createMutation.mutate(proposedRun);
    };

    const toggleVariant = (variantId: number) =>
        patchForm({
            selectedVariantIds: form.selectedVariantIds.includes(variantId)
                ? form.selectedVariantIds.filter((id) => id !== variantId)
                : [...form.selectedVariantIds, variantId]
        });

    const toggleHorizon = (horizon: number) => {
        const base = form.selectedHorizons ?? defaultHorizons;

        patchForm({
            selectedHorizons: base.includes(horizon)
                ? base.filter((value) => value !== horizon)
                : [...base, horizon].sort((left, right) => left - right)
        });
    };

    return (
        <Modal
            footer={
                <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-amber-300">{validation ?? ''}</span>
                    <div className="flex gap-2">
                        <Button variant="secondary" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            disabled={proposedRun === null || isPreviewing}
                            isLoading={isPreviewing}
                            variant="outline-dark"
                            onClick={() => void loadPreview()}
                        >
                            Preview workload
                        </Button>
                        <Button
                            disabled={validation !== null || createMutation.isPending}
                            isLoading={createMutation.isPending}
                            variant="primary"
                            onClick={submit}
                        >
                            Queue run
                        </Button>
                    </div>
                </div>
            }
            isOpen={isOpen}
            size="xl"
            title={sourceRunId === null ? 'New run' : `New run from #${sourceRunId}`}
            onClose={onClose}
        >
            {isLoadingSource ? (
                sourceRunQuery.error instanceof Error ? (
                    <Callout variant="error">{sourceRunQuery.error.message}</Callout>
                ) : (
                    <DataLoadingSpinner text="Loading the run to copy..." />
                )
            ) : (
                <div className="space-y-5">
                    {sourceRun && (
                        <Callout variant="info">
                            Every setting is copied from run #{sourceRun.id} &ldquo;{sourceRun.label}&rdquo;. Change what you want to test,
                            rename the run, then queue it.
                            {droppedVariantCount > 0 &&
                                ` ${droppedVariantCount} of its variants ${droppedVariantCount === 1 ? 'is' : 'are'} archived and left out.`}
                        </Callout>
                    )}

                    <FormInput
                        label="Label"
                        maxLength={255}
                        noMargin
                        placeholder="e.g. Tech list, 2024 H1"
                        value={form.label}
                        onChange={(event) => patchForm({ label: event.target.value })}
                    />

                    <section>
                        <h4 className="mb-2 text-sm font-semibold text-zinc-200">Variants</h4>
                        {frozen.length === 0 ? (
                            <Callout variant="warning">No frozen variants yet. Freeze a variant on its page before running.</Callout>
                        ) : (
                            <div className="grid gap-2 sm:grid-cols-2">
                                {frozen.map((variant) => (
                                    <label
                                        className="flex cursor-pointer items-start gap-2 rounded border border-zinc-800 bg-black/20 px-3 py-2"
                                        key={variant.id}
                                    >
                                        <input
                                            checked={form.selectedVariantIds.includes(variant.id)}
                                            className="mt-1 rounded border-zinc-700 bg-zinc-900 text-blue-500 focus:ring-blue-500"
                                            type="checkbox"
                                            onChange={() => toggleVariant(variant.id)}
                                        />
                                        <span className="text-sm">
                                            <span className="text-zinc-100">{variant.label}</span>
                                            <span className="ml-2 text-zinc-500">
                                                {variant.variantKey} · #{variant.id} · {variant.primaryDuration}
                                                {variant.isStudyControl ? ' · control' : ''}
                                            </span>
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </section>

                    <section>
                        <div className="mb-2 flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-zinc-200">Universe</h4>
                            <SegmentedControl
                                ariaLabel="Universe source"
                                options={UNIVERSE_MODE_OPTIONS}
                                value={form.universeMode}
                                onChange={(universeMode) => patchForm({ universeMode })}
                            />
                        </div>
                        {form.universeMode === 'entities' ? (
                            <EntitySelectionPanel
                                entityListId={form.entityListId}
                                selectedEntities={form.selectedEntities}
                                onEntitiesChange={(selectedEntities) => patchForm({ selectedEntities })}
                                onEntityListIdChange={(entityListId) => patchForm({ entityListId })}
                            />
                        ) : (
                            <ScreenerUniverseSelector
                                entityTypes={form.screenerEntityTypes}
                                screenerRuleId={form.screenerRuleId}
                                onEntityTypesChange={(screenerEntityTypes) => patchForm({ screenerEntityTypes })}
                                onScreenerRuleIdChange={(screenerRuleId) => patchForm({ screenerRuleId })}
                            />
                        )}
                        <p className="mt-2 text-xs text-zinc-500">
                            Select stocks, indexes, cryptocurrencies, or a mixed entity list. Options support stocks and indexes.
                        </p>
                        {form.includeOptions && (
                            <p className="mt-2 text-xs text-zinc-500">
                                Index options use the same option buying strategy and spread checks as stock options. Choose horizons that
                                close before the contract’s last trading time; index expiration settlement is not supported. Random-entry
                                option reference baselines are currently available for stocks only.
                            </p>
                        )}
                    </section>

                    <section>
                        <h4 className="mb-2 text-sm font-semibold text-zinc-200">Window (Eastern)</h4>
                        <div className="grid gap-3 sm:grid-cols-2">
                            <div className="grid grid-cols-2 gap-2">
                                <FormDateInput
                                    label="Start"
                                    noMargin
                                    value={form.start.date}
                                    onChange={(event) => patchForm({ start: { ...form.start, date: event.target.value } })}
                                />
                                <FormTimeInput
                                    label="Time"
                                    noMargin
                                    step="1"
                                    value={form.start.time}
                                    onChange={(event) => patchForm({ start: { ...form.start, time: event.target.value } })}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <FormDateInput
                                    label="End"
                                    noMargin
                                    value={form.end.date}
                                    onChange={(event) => patchForm({ end: { ...form.end, date: event.target.value } })}
                                />
                                <FormTimeInput
                                    label="Time"
                                    noMargin
                                    step="1"
                                    value={form.end.time}
                                    onChange={(event) => patchForm({ end: { ...form.end, time: event.target.value } })}
                                />
                            </div>
                        </div>
                        <div className="mt-3">
                            <FormCheckbox
                                checked={form.useWalkForward}
                                label="Seal a walk-forward window (training results exclude outcomes that complete after this point)"
                                onChange={(event) => patchForm({ useWalkForward: event.target.checked })}
                            />
                            {form.useWalkForward && (
                                <div className="mt-2 grid max-w-md grid-cols-2 gap-2">
                                    <FormDateInput
                                        label="Walk-forward start"
                                        noMargin
                                        value={form.walkForwardStart.date}
                                        onChange={(event) =>
                                            patchForm({ walkForwardStart: { ...form.walkForwardStart, date: event.target.value } })
                                        }
                                    />
                                    <FormTimeInput
                                        label="Time"
                                        noMargin
                                        step="1"
                                        value={form.walkForwardStart.time}
                                        onChange={(event) =>
                                            patchForm({ walkForwardStart: { ...form.walkForwardStart, time: event.target.value } })
                                        }
                                    />
                                </div>
                            )}
                        </div>
                        <p className="mt-2 text-xs text-zinc-500">
                            Outcomes are priced only up to the window end or the latest settled market date, whichever is earlier.
                        </p>
                    </section>

                    <section>
                        <h4 className="mb-2 text-sm font-semibold text-zinc-200">Horizons{duration ? ` (${duration} menu)` : ''}</h4>
                        {duration === null ? (
                            <p className="text-sm text-zinc-500">Select variants of one duration to choose horizons.</p>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {horizonMenu.map((horizon) => (
                                    <label
                                        className="flex cursor-pointer items-center gap-2 rounded border border-zinc-800 bg-black/20 px-3 py-1.5 text-sm"
                                        key={horizon}
                                    >
                                        <input
                                            checked={effectiveHorizons.includes(horizon)}
                                            className="rounded border-zinc-700 bg-zinc-900 text-blue-500 focus:ring-blue-500"
                                            type="checkbox"
                                            onChange={() => toggleHorizon(horizon)}
                                        />
                                        <span className="text-zinc-200">{formatHorizonLabel(horizon)}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </section>

                    <section>
                        <h4 className="mb-2 text-sm font-semibold text-zinc-200">Lenses</h4>
                        <div className="space-y-2">
                            <FormCheckbox
                                checked={form.includeUnderlying}
                                label="Underlying: fixed-horizon returns of the stock, index, or coin itself"
                                onChange={(event) => patchForm({ includeUnderlying: event.target.checked, includeFutures: false })}
                            />
                            <FormCheckbox
                                checked={form.includeOptions}
                                label="Options: one contract per entry, selected the way a strategy would, replayed to each horizon with platform commissions"
                                onChange={(event) => patchForm({ includeOptions: event.target.checked, includeFutures: false })}
                            />
                            <FormCheckbox
                                checked={form.includeFutures}
                                label="Futures: SPY→MES or QQQ→MNQ with native contract costs"
                                onChange={(event) =>
                                    patchForm({
                                        includeFutures: event.target.checked,
                                        includeUnderlying: !event.target.checked,
                                        includeOptions: false,
                                        afterHoursRemoved: true,
                                        sessionTimingMode: event.target.checked ? 'nextOpen' : 'recommended',
                                        selectedHorizons: event.target.checked ? [900] : null
                                    })
                                }
                            />
                            {form.includeFutures && (
                                <div className="space-y-4">
                                    <Callout variant="info">
                                        Choose SPY, QQQ, or both above. Each signal is an independent native futures opportunity. Positions
                                        close by the fixed horizon or the mandatory session deadline; portfolio cash and occupancy are
                                        evaluated after promotion.
                                    </Callout>
                                    {form.selectedEntities.map((entity) => {
                                        const route = futuresRoutes.data?.find(
                                            (route) => entity.entityType === 'Asset' && route.signalAssetId === entity.entityId
                                        );
                                        if (!route) return null;
                                        return (
                                            <div key={entity.entityId}>
                                                <h5>
                                                    {route.signalSymbol} → {route.productSymbol}
                                                </h5>
                                                <FuturesConfigurationFields
                                                    allowedProductIds={[route.productId]}
                                                    value={
                                                        form.futuresDrafts[entity.entityId] ?? {
                                                            ...emptyFuturesConfiguration(),
                                                            productId: route.productId
                                                        }
                                                    }
                                                    onChange={(draft) =>
                                                        patchForm({ futuresDrafts: { ...form.futuresDrafts, [entity.entityId]: draft } })
                                                    }
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {form.includeOptions && (
                                <div className="ml-6 space-y-2">
                                    <OptionBuyingStrategySelect
                                        helpText="Frozen to its call/put selection archetypes at queue time; a promoted strategy pins this exact strategy on its option leg."
                                        label="Option buying strategy"
                                        mode="single"
                                        noMargin
                                        value={form.optionBuyingStrategyId}
                                        onChange={(optionBuyingStrategyId) => patchForm({ optionBuyingStrategyId })}
                                    />
                                    <FormSelect
                                        label="Option type"
                                        value={form.optionType ?? ''}
                                        options={[
                                            { value: '', label: 'Select Call or Put' },
                                            { value: 'call', label: 'Call' },
                                            { value: 'put', label: 'Put' }
                                        ]}
                                        onChange={(event) =>
                                            patchForm({
                                                optionType:
                                                    event.target.value === 'call' ? 'call' : event.target.value === 'put' ? 'put' : null
                                            })
                                        }
                                    />
                                    <p className="text-xs text-zinc-500">
                                        Returns are net dollars of one contract as a percentage of its opening premium. Stocks and indexes
                                        are supported; the strategy must already be certified to selection archetypes (new strategies are
                                        certified by the nightly archetype sync).
                                    </p>
                                    {duration !== null && !optionBaselinesAvailable && (
                                        <Callout variant="warning">
                                            Option random-entry references exist only for 30m, 60m, 240m, and 1d durations; the {duration}{' '}
                                            option lens will report returns without a contextual random edge.
                                        </Callout>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>

                    <section>
                        <FormCheckbox
                            checked={form.afterHoursRemoved}
                            label="Stocks and indexes: regular session only"
                            onChange={(event) => patchForm({ afterHoursRemoved: event.target.checked })}
                        />
                        {minuteDuration && !form.afterHoursRemoved && (
                            <Callout className="mt-2" variant="warning">
                                With after-hours entries kept, the contextual random-entry reference is unavailable: platform baselines
                                cover the regular session only.
                            </Callout>
                        )}
                    </section>

                    <section>
                        <h4 className="mb-2 text-sm font-semibold text-zinc-200">Entry timing</h4>
                        <div className="grid gap-3 sm:grid-cols-2">
                            <FormSelect
                                label="Session-close behavior"
                                noMargin
                                options={SESSION_TIMING_OPTIONS}
                                value={form.sessionTimingMode}
                                onChange={(event) => patchForm({ sessionTimingMode: parseSessionTimingMode(event.target.value) })}
                            />
                            {form.sessionTimingMode === 'nearClose' && (
                                <FormNumberInput
                                    integer
                                    label="Seconds before close"
                                    max={120}
                                    min={0}
                                    noMargin
                                    value={form.preCloseOffsetSecs}
                                    onValueChange={(preCloseOffsetSecs) => patchForm({ preCloseOffsetSecs })}
                                />
                            )}
                        </div>
                        <p className="mt-2 text-xs text-zinc-500">
                            Timing is frozen separately for each market. Exchange settings apply to stocks and indexes; crypto always uses
                            24/7 completed bars. Stock/index regular-session 30m, 60m, 120m, 180m, 240m, and daily runs use 120 seconds
                            before close. Preview the workload to see the exact policy.
                        </p>
                    </section>

                    {preview && (
                        <section className="rounded border border-zinc-700 bg-zinc-900/60 p-4">
                            <h4 className="text-sm font-semibold text-zinc-100">Resolved workload</h4>
                            <div className="mt-3 grid gap-3 sm:grid-cols-3">
                                <div>
                                    <div className="text-xl font-semibold text-zinc-100">{preview.entityCount}</div>
                                    <div className="text-xs text-zinc-500">entities · {preview.workUnitCount} work units</div>
                                </div>
                                <div>
                                    <div className="text-xl font-semibold text-zinc-100">{preview.pooledResultCellEstimate}</div>
                                    <div className="text-xs text-zinc-500">pooled result cells</div>
                                </div>
                                <div>
                                    <div className="text-xl font-semibold text-zinc-100">{preview.entityResultCellEstimate}</div>
                                    <div className="text-xs text-zinc-500">entity result cells</div>
                                </div>
                            </div>
                            <p className="mt-3 text-xs text-zinc-400">
                                {preview.variantCount} variants × {preview.horizonCount} horizons × {preview.groups.length} market groups ×{' '}
                                {preview.windowCount} windows at {preview.primaryDuration}.
                            </p>
                            <p className="mt-1 text-xs text-zinc-400">
                                Entry timing:{' '}
                                {preview.markets.map((market) => (
                                    <span className="block" key={market.entityType}>
                                        {market.entityType}:{' '}
                                        {describeSessionTimingPolicy(
                                            preview.primaryDuration,
                                            market.sessionTimingPolicy[preview.primaryDuration]
                                        )}
                                    </span>
                                ))}
                                .
                            </p>
                            <ul className="text-sm text-zinc-300">
                                {preview.groups.map((group) => (
                                    <li key={`${group.entityType}:${group.lens}`}>
                                        {marketGroupLabel(group.entityType, group.lens)}: {group.entityCount} entities
                                    </li>
                                ))}
                            </ul>
                            {preview.warnings.map((warning) => (
                                <Callout className="mt-2" key={warning} variant="warning">
                                    {warning}
                                </Callout>
                            ))}
                        </section>
                    )}
                </div>
            )}
        </Modal>
    );
};

export default CreateRunModal;
